// An open call in Blueprint (calls round, stage 5): its own page, CallPage, titled with who it is, the
// crumb back to Calls. The call's parts on the left and its live transcript beside them
// (CallLiveTranscript). What it holds and reads is use-calls-screen.ts's. The left column is still
// today's parts, and the booking Book.tsx's form, until BL-189's second and third Calls PRs move them
// onto catalogued components.
import type { ReactNode } from 'react';
import { CallPage, Link } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import type { NavProps } from '../Nav.tsx';
import { LoadLine } from '../shared/Ladder.tsx';
import { NewCustomerForm } from '../shared/NewCustomer.tsx';
import { phoneText, textablePhone, type Call, type Customer, type JobRow } from '../types.ts';
import { openJob } from '../job/job-page.ts';
import { CallPayment } from './CallPayment.tsx';
import { CallReasonPart } from './CallReason.tsx';
import { MIRROR_LINE } from '../shared/mirror.ts';
import { CallGlance } from './CallGlance.tsx';
import { campaignWord, lineWord } from './source-model.ts';
import { CallTranscript } from './CallTranscript.tsx';
import { openJobsFirst, type Glance } from './glance-model.ts';
import { BookForm } from '../Book.tsx';
import { plainText } from '../shared/plain-text.ts';
import { jobStatusWord } from '@flowology/ui-web/words';
import { AdClickOf } from '../shared/AdClick.tsx';
import { CallLiveTranscript } from './CallLiveTranscript.tsx';
import { MatchRecordings } from './MatchRecordings.tsx';
import { CALL_NOTE, type CallsScreenState } from './use-calls-screen.ts';
import '../calls.css';
import '../blueprint/inbox.css';

function ConsentChip({ c }: { c: Customer }) {
  const t = textablePhone(c);
  if (c.doNotContact) return <span className="chip say bad">Do not contact</span>;
  return 'method' in t ? (
    <span className="chip say ok">OK to text</span>
  ) : (
    <span className="chip say warn">No texts</span>
  );
}

function CustomerCard({
  c,
  jobs,
  glance,
  payment,
  timeZone,
}: {
  c: Customer;
  jobs: JobRow[];
  glance: { g: Glance | null; owingCents?: number } | null;
  payment?: ReactNode;
  timeZone: string;
}) {
  const loc = c.locations[0];
  const contact = c.contacts[0];
  const phone = contact?.methods.find((m) => m.kind === 'phone');
  // Blueprint joins with commas, never a middle dot
  return (
    <section className="stone card">
      <h2 className="h3">{c.displayName}</h2>
      <div className="ink2">
        {contact?.name}
        {phone && `, ${phoneText(phone.value)}`}
      </div>
      {loc && (
        <div className="ink2">
          {loc.line1}, {loc.city}
          {c.locations.length > 1 &&
            `, +${c.locations.length - 1} more location${c.locations.length > 2 ? 's' : ''}`}
        </div>
      )}
      <div className="tagrow">
        <ConsentChip c={c} />
        <span className="chip say">Customer {c.number}</span>
        {c.kind === 'commercial' && <span className="chip say">Commercial</span>}
      </div>
      {loc?.accessNotes && <p className="ink2">Access: {loc.accessNotes}</p>}
      {glance && <CallGlance g={glance.g} owingCents={glance.owingCents} timeZone={timeZone} />}
      <h3 className="h4">Recent jobs</h3>
      {jobs.length ? (
        <ul className="hist">
          {openJobsFirst(jobs).map((j) => (
            <li key={j.id}>
              {/* the job sheet: its estimates, and Cancel */}
              <button type="button" className="row-btn" onClick={() => openJob(j.id)}>
                <span className="who-col">
                  <b>#{j.number}</b> {plainText(j.summary)}
                </span>
                <span className="chip say">{jobStatusWord(j.status)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No jobs yet.</p>
      )}
      {payment}
    </section>
  );
}

function NewCustomer({ call, onCreated }: { call: Call; onCreated: (customerId: string) => void }) {
  return (
    <section className="stone card">
      <h2 className="h3">No customer has {phoneText(call.from)}</h2>
      <h3 className="h4">New customer</h3>
      <NewCustomerForm
        phone={call.from}
        who="this caller"
        consentNote="Consent (asked on this call; saved with who and when)"
        submitLabel={<>Create customer &amp; book</>}
        link={(customerId, idempotencyKey) =>
          client.call(
            'calls.update',
            { id: call.id, customerId },
            idempotencyKey ? { idempotencyKey } : undefined,
          )
        }
        onDone={onCreated}
      />
    </section>
  );
}

export function OpenCall({
  s,
  nav,
  callId,
  onOpenCall,
  onBoard,
}: {
  s: CallsScreenState;
  nav: NavProps;
  callId: string;
  onOpenCall: (id: string | null) => void;
  onBoard: () => void;
}) {
  const { call, customer, mirroring, held, everyone, records, clockOf, whenOf, zone, notes } = s;
  const bar = (w = '') => <span className={`ph-bar ${w}`} aria-hidden="true" />;
  // the open call: what it booked, the booking form, its cards and its pinned actions
  const detail = (
    <>
      {s.callPending && (
        // L-sheet: the pop's place and the call's frame at once; the words arrive together
        <div className={s.callPhase === 'wait' ? 'ldwait' : ''} aria-busy={s.callPhase !== 'error'}>
          <div className="stone now-ph" aria-hidden="true">
            {bar('w40')}
            {bar('w60')}
          </div>
          <div className="grid2">
            <section className="stone card cust-ph">
              {s.callPhase !== 'error' && (
                <>
                  {bar('w60 head')}
                  {bar('w40')}
                  {bar('w40')}
                </>
              )}
            </section>
            <section className="stone card">
              <h2 className="h4">This call</h2>
              <dl className="kv">
                {['From', 'Line', 'Started', 'Outcome'].map((k) => (
                  <div key={k} className="kv-ph">
                    <dt>{k}</dt>
                    <dd>{s.callPhase !== 'error' && bar('w40')}</dd>
                  </div>
                ))}
              </dl>
              <label className="f">
                Call notes (saved on the call)
                <span className="field area ph-field" aria-hidden="true" />
              </label>
              <div className="row">
                <button className="btn primary" disabled>
                  Book a job
                </button>
                <button className="btn" disabled>
                  Not booked
                </button>
              </div>
              {/* the call's one-line note is the slot: "Getting the call…" takes its place at 3 s */}
              <LoadLine phase={s.callPhase} what="the call" note={CALL_NOTE} onRetry={s.retryCall} />
            </section>
          </div>
        </div>
      )}

      {!s.callPending && call && s.booking && (
        // the Book screen's form, beside the queue (BL-101): its rules are the Book screen's
        <section className="call-book" ref={s.bookingRef} aria-label="Book a job for this call">
          <div className="row between">
            <h2 className="h3">Book a job</h2>
            <button type="button" className="btn quiet" onClick={s.stopBooking}>
              Back to the call
            </button>
          </div>
          <BookForm nav={nav} start={s.booking} timeZone={zone} onBoard={onBoard} onBooked={s.onBooked} />
        </section>
      )}

      {!s.callPending && call && !s.booking && (
        <div className="grid2">
          {customer ? (
            <CustomerCard
              c={customer}
              jobs={s.jobs}
              timeZone={zone}
              glance={
                s.glance && {
                  g: s.glance.g,
                  owingCents: s.owing?.key === s.glance.key ? s.owing.cents : undefined,
                }
              }
              // payments.record (and its pay link) is the office's (F-40)
              payment={
                !mirroring && held.includes('payments.record') ? (
                  <CallPayment customer={customer} timeZone={zone} tick={s.moneyTick} />
                ) : undefined
              }
            />
          ) : call.matches.length > 1 ? (
            <section className="stone card">
              <h2 className="h3">Who is calling?</h2>
              <p className="ink2">Several customers have {phoneText(call.from)}.</p>
              {mirroring && <p className="muted small">{MIRROR_LINE}</p>}
              {!mirroring ? (
                // the customers as rows to pick from, one action each
                <ul className="calls">
                  {call.matches.map((m) => (
                    <li key={m.customerId + m.contactName}>
                      <button
                        className="row-btn"
                        onClick={() => void s.update({ customerId: m.customerId }).then(s.loadCall)}
                      >
                        <span className="ib-crow-who">
                          <b>{m.customerName}</b>
                          <span className="ib-sub">{m.contactName}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                call.matches.map((m) => (
                  <p key={m.customerId + m.contactName} className="small">
                    {m.contactName} · {m.customerName}
                  </p>
                ))
              )}
            </section>
          ) : mirroring ? (
            <section className="stone card">
              <h2 className="h3">No customer has {phoneText(call.from)}</h2>
              <p className="muted small">{MIRROR_LINE}</p>
            </section>
          ) : (
            <NewCustomer
              call={call}
              onCreated={(id) => {
                s.startBooking({ customerId: id, callId: call.id, notes: notes || null });
                void s.loadCall();
              }}
            />
          )}

          <section className="stone card">
            <h2 className="h4">This call</h2>
            <dl className="kv">
              <dt>From</dt>
              <dd>{phoneText(call.from)}</dd>
              <dt>Line</dt>
              <dd>{lineWord(phoneText(call.to), call.trackingLabel)}</dd>
              {/* where it came from (call-handling item 3, BL-008) */}
              <dt>Source</dt>
              <dd>
                {call.campaign ? campaignWord(call.campaign) : <span className="muted">No campaign</span>}
              </dd>
              <dt>Started</dt>
              <dd>{clockOf(call.startedAt)}</dd>
              {s.callJob && call.jobId && (
                <>
                  <dt>Job</dt>
                  <dd>
                    <button
                      type="button"
                      className="btn quiet sm call-job"
                      onClick={() => openJob(call.jobId!)}
                    >
                      #{s.callJob}
                    </button>
                  </dd>
                </>
              )}
              {call.textedBackAt && (
                <>
                  <dt>Texted back</dt>
                  <dd>{clockOf(call.textedBackAt)}</dd>
                </>
              )}
            </dl>
            {/* the ad click it came from, by the website's tag or the AdTrack import (BL-092) */}
            <AdClickOf of={{ callId: call.id }} timeZone={zone} />
            {/* booked or why not, who took it, a review (BL-003) */}
            <CallReasonPart
              call={call}
              held={held}
              onCall={s.setCall}
              toast={s.setToast}
              pickSignal={s.pickSignal}
              pinned
            />
            {/* the Twilio recording and what was said: owner and managers (BL-083, AD-9) */}
            {everyone && <CallTranscript key={call.id} callId={call.id} known={call.recording} />}
            <label className="f">
              {records ? 'Call notes (saved on the call)' : 'Call notes'}
              <textarea
                className="field area"
                value={notes}
                readOnly={!records}
                placeholder={records ? 'What did they say?' : undefined}
                onChange={(e) => s.typeNotes(call.id, e.target.value)}
                onBlur={() => void s.saveNotes(call.id)}
              />
            </label>
            <p className="muted small">{CALL_NOTE}</p>
          </section>
        </div>
      )}

      {!s.callPending && call && !s.booking && (
        // the call's main actions, pinned at the foot of the screen (BL-099): in view at any height
        <div className="call-actions" role="group" aria-label="This call’s actions">
          {customer && !mirroring && (
            <button
              className="btn primary"
              onClick={() =>
                // the call's address and its notes, as typed (calls item 5)
                s.startBooking({
                  customerId: customer.id,
                  callId: call.id,
                  locationId: call.locationId,
                  notes: notes || null,
                })
              }
            >
              Book a job
            </button>
          )}
          {records && call.outcome !== 'booked' && !(call.reviewed && !everyone) && (
            <button type="button" className="ib-link" onClick={s.pickReason}>
              {call.reason ? 'Change the reason…' : 'Not booked…'}
            </button>
          )}
          {records && !call.takenBy && call.outcome !== 'booked' && (
            <button
              type="button"
              className="ib-link"
              onClick={() => void s.update({ claim: true }, 'It’s yours to call back.')}
            >
              I’ll call them back
            </button>
          )}
          <button type="button" className="ib-link" onClick={() => onOpenCall(null)}>
            Close
          </button>
        </div>
      )}
    </>
  );

  // what the call booked, said once over the page's columns
  const bookedNotice = !s.callPending && call && s.booked && (
    <p className="notice ok" role="status">
      <b>Booked: job #{s.booked.jobNumber}.</b> It’s on the dispatch board
      {s.booked.texted ? ', and the confirmation text is on its way' : ''}.{' '}
      <button type="button" className="ib-link" onClick={() => openJob(s.booked!.jobId)}>
        Open the job
      </button>
    </p>
  );
  // under the caller's name, how the call stands (no phone in the app: it's answered on one)
  const stateLine =
    !s.callPending && call
      ? `${
          call.status === 'ringing'
            ? 'Ringing'
            : call.status === 'in_progress'
              ? call.answeredBy
                ? `${call.answeredBy} answered`
                : 'Answered on a phone'
              : (s.status?.label ?? call.status)
        }. ${phoneText(call.from)} to ${phoneText(call.to)}, ${clockOf(call.startedAt).replace(/\.$/, '')}. ${
          customer
            ? 'Matched to a customer.'
            : call.matches.length > 1
              ? 'Pick the customer.'
              : 'No customer has this number.'
        }`
      : null;

  return (
    <CallPage
      crumb={
        <>
          Inbox › <Link onPress={() => onOpenCall(null)}>Calls</Link>
        </>
      }
      title={call && call.id === callId && s.who ? s.who.replace(' · ', ', ') : 'A call'}
      {...(stateLine ? { sub: stateLine } : {})}
      {...(bookedNotice ? { notice: bookedNotice } : {})}
      main={
        <div className="ib-call calls-bp-main">
          {detail}
          {everyone && <MatchRecordings whenOf={whenOf} toast={s.setToast} />}
        </div>
      }
      side={call && !s.callPending ? <CallLiveTranscript call={call} /> : null}
      sideLabel="Transcript"
      settle={s.settle}
    />
  );
}
